// src/RequireAuth.jsx
import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Box, CircularProgress, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button } from '@mui/material';
import { supabase } from './supabaseClient';
import useIdleLogout from './useIdleLogout';

const RequireAuth = ({ children }) => {
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [session, setSession] = useState(null);

  // Thirty minutes idle and the session goes. Warned a minute before, because
  // a half-written resolution note disappearing without a word is its own
  // small betrayal.
  const { secondsLeft, staySignedIn } = useIdleLogout({ idleMinutes: 30, warnSeconds: 60 });

  useEffect(() => {
    let isMounted = true;

    async function load() {
      try {
        // If we just returned from OAuth, exchange the code explicitly.
        // This makes the flow robust even if auto-detect fails.
        const url = window.location.href;
        const hasCode = /[?&]code=/.test(window.location.search);
        const hasError = /[?&]error=/.test(window.location.search);

        if (hasError) {
          console.error('[RequireAuth] OAuth error in URL:', window.location.search);
        }

        if (hasCode) {
          try {
            const { error } = await supabase.auth.exchangeCodeForSession(url);
            if (error) {
              console.error('[RequireAuth] exchangeCodeForSession failed:', error);
            } else {
              // Remove code from URL to avoid re-exchanging on refresh.
              window.history.replaceState({}, document.title, window.location.pathname);
            }
          } catch (e) {
            console.error('[RequireAuth] exchangeCodeForSession exception:', e);
          }
        }

        const { data } = await supabase.auth.getSession();
        if (isMounted) setSession(data.session);
      } finally {
        if (isMounted) setLoading(false);
      }
    }

    load();

    const { data: sub } = supabase.auth.onAuthStateChange((_event, nextSession) => {
      if (!isMounted) return;
      setSession(nextSession);
      setLoading(false);
    });

    return () => {
      isMounted = false;
      sub?.subscription?.unsubscribe?.();
    };
  }, []);

  if (loading) {
    return (
      <Box sx={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!session) {
    return <Navigate to="/" state={{ from: location }} replace />;
  }

  return (
    <>
      {children}
      <Dialog open={secondsLeft !== null} onClose={staySignedIn}>
        <DialogTitle>Still there?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            You have been idle, so you will be signed out in {secondsLeft ?? 0} seconds.
            This console shows clients&rsquo; personal details, so it does not stay open on its own.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={staySignedIn} variant="contained">Stay signed in</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default RequireAuth;
